// Quality Assurance tab (B6) — raw HTTP transport for LoanQaReview
// lifecycle + the QA-scoped report actions, plus the admin scope-area
// catalog. Areas are now SERVER-driven (admin-managed catalog), so there is
// no hardcoded area list here. Hooks in @/hooks/portfolioAdmin/useQaReviews
// wrap these with TanStack Query.

import { api } from "../client";
import type { LoanReportDto } from "./loanReportsApi";

// 5-state per-area examined status (replaces the old Unset/Checked/NotApplicable).
export type QaScopeState =
  | "NotExamined"
  | "Pass"
  | "PassWithObservations"
  | "Findings"
  | "NA";

export const QA_SCOPE_STATE_LABELS: Record<QaScopeState, string> = {
  NotExamined: "Not examined",
  Pass: "Pass",
  PassWithObservations: "Pass with observations",
  Findings: "Findings",
  NA: "N/A",
};

// Per-checklist-item status; "" = not yet addressed.
export type QaChecklistStatus = "" | "Verified" | "Issue" | "NA";

export const QA_CHECKLIST_STATUS_LABELS: Record<QaChecklistStatus, string> = {
  "": "Not addressed",
  Verified: "Verified",
  Issue: "Issue",
  NA: "N/A",
};

export type QaFindingSeverity = "Observation" | "Exception";
export type QaReviewStatus = "Draft" | "Completed";
export type QaCompletionStamp = "Full" | "Partial";

export interface LoanQaChecklistItemDto {
  itemCode: string;
  label: string;
  helpText: string | null;
  required: boolean;
  status: QaChecklistStatus;
}

export interface LoanQaScopeAreaDto {
  area: string;
  name: string;
  purpose: string | null;
  state: QaScopeState;
  checklist: LoanQaChecklistItemDto[];
}

export interface LoanQaFindingDto {
  id: string;
  area: string;
  severity: QaFindingSeverity;
  description: string;
  followUpTaskId: string | null;
  /** Read-only WorkTask status ("Open" | "InProgress" | "Done"); null when
   *  no follow-up task exists. */
  followUpTaskStatus: string | null;
}

export interface LoanQaReviewDto {
  id: string;
  loanId: string;
  status: QaReviewStatus;
  reviewDate: string;
  reviewedByUserName: string | null;
  scope: LoanQaScopeAreaDto[];
  findings: LoanQaFindingDto[];
  narrative: string | null;
  reportLoanReportId: string | null;
  reportStatus: string | null;
  completedAt: string | null;
  openExceptionCount: number;
  /** "Full" | "Partial" | null (pre-feature / still draft). */
  completionStamp: QaCompletionStamp | null;
  /** Required checklist items still unaddressed (live for Draft, snapshot
   *  for Completed). */
  unresolvedRequiredCount: number;
}

export interface LoanQaCadenceDto {
  itemId: string;
  dueDate: string;
  status: string;
}

export interface LoanQaOverviewDto {
  reviews: LoanQaReviewDto[];
  lastCompletedReviewDate: string | null;
  openExceptionCount: number;
  observationCount: number;
  exceptionCount: number;
  cadence: LoanQaCadenceDto | null;
}

export interface UpdateLoanQaReviewRequest {
  reviewDate?: string | null;
  scope: {
    area: string;
    state: QaScopeState;
    checklist: { itemCode: string; status: QaChecklistStatus }[];
  }[];
  findings: {
    id: string;
    area: string;
    severity: QaFindingSeverity;
    description: string;
  }[];
  narrative: string | null;
}

export const qaReviewsApi = {
  overview: (loanId: string) =>
    api.get<LoanQaOverviewDto>(`/portfolio-admin/loans/${loanId}/qa-reviews`),
  start: (loanId: string) =>
    api.post<LoanQaReviewDto>(`/portfolio-admin/loans/${loanId}/qa-reviews`),
  update: (reviewId: string, body: UpdateLoanQaReviewRequest) =>
    api.put<LoanQaReviewDto>(`/portfolio-admin/qa-reviews/${reviewId}`, body),
  complete: (reviewId: string) =>
    api.post<LoanQaReviewDto>(`/portfolio-admin/qa-reviews/${reviewId}/complete`),
  createFollowUpTask: (reviewId: string, findingId: string) =>
    api.post<LoanQaReviewDto>(
      `/portfolio-admin/qa-reviews/${reviewId}/findings/${findingId}/follow-up-task`,
    ),
  generateReport: (reviewId: string) =>
    api.post<LoanQaReviewDto>(`/portfolio-admin/qa-reviews/${reviewId}/report`),
  certifyReport: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/qa-reports/${reportId}/certify`),
  fileReport: (reportId: string) =>
    api.post<LoanReportDto>(`/portfolio-admin/qa-reports/${reportId}/file`),
};

// ----- scope-area catalog (admin) ------------------------------------------

export interface LoanQaChecklistItemDefinitionDto {
  id: string;
  itemCode: string;
  label: string;
  helpText: string | null;
  required: boolean;
  active: boolean;
  displayOrder: number;
}

export interface LoanQaScopeAreaCatalogItemDto {
  id: string;
  code: string;
  name: string;
  purpose: string | null;
  ownerRoleCode: string;
  active: boolean;
  onByDefault: boolean;
  displayOrder: number;
  checklistItems: LoanQaChecklistItemDefinitionDto[];
}

export interface UpsertLoanQaChecklistItemRequest {
  id?: string | null;
  label: string;
  helpText?: string | null;
  required: boolean;
}

export interface CreateLoanQaScopeAreaRequest {
  name: string;
  purpose?: string | null;
  ownerRoleCode: string;
  onByDefault: boolean;
  checklistItems: UpsertLoanQaChecklistItemRequest[];
}

export interface UpdateLoanQaScopeAreaCatalogRequest {
  name: string;
  purpose?: string | null;
  ownerRoleCode: string;
  onByDefault: boolean;
  active: boolean;
  checklistItems: UpsertLoanQaChecklistItemRequest[];
}

export const qaScopeAreasApi = {
  list: (includeInactive = false) =>
    api.get<LoanQaScopeAreaCatalogItemDto[]>(
      `/portfolio-admin/qa-scope-areas?includeInactive=${includeInactive}`,
    ),
  create: (body: CreateLoanQaScopeAreaRequest) =>
    api.post<LoanQaScopeAreaCatalogItemDto>(`/portfolio-admin/qa-scope-areas`, body),
  update: (id: string, body: UpdateLoanQaScopeAreaCatalogRequest) =>
    api.patch<LoanQaScopeAreaCatalogItemDto>(
      `/portfolio-admin/qa-scope-areas/${id}`,
      body,
    ),
  reorder: (orderedIds: string[]) =>
    api.post<boolean>(`/portfolio-admin/qa-scope-areas/reorder`, { orderedIds }),
};
